import { 
    getUserProfile, 
    getCompanyProfileForUser, 
    getCompanyProfile,
    CompanyProfile,
    UserProfile,
    CustomTool
} from './authService';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../config/firebase';
import { logger } from '../utils/logger';
import { DEFAULT_COMPANY_LOGO_PATH, DEFAULT_COMPANY_LOGO_URL } from '../constants/storage';

export interface CachedUserData {
    timestamp: number;
    userProfile: UserProfile | null;
    companyProfile: CompanyProfile | null;
    cachedImages: { [key: string]: string }; // URL -> blob URL mapping
    companyLogoDataUrl: string | null; // Company logo as data URL (loaded once at login)
}

const CACHE_PREFIX = 'user_cache_';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Cache user data after login
export const cacheUserData = async (userId: string): Promise<void> => {
    try {
        logger.log('[Cache] Starting user data caching for:', userId);
        
        // Load user profile
        const userProfile = await getUserProfile(userId);
        
        // Load company profile
        const companyProfile = await getCompanyProfileForUser(userId);
        
        const cachedImages: { [key: string]: string } = {};
        
        if (companyProfile && companyProfile.tools) {
            const customTools = companyProfile.tools.filter(tool => tool.type === 'custom');
            const loadPromises = customTools
                .filter(tool => tool.imageUrl)
                .map(async (customTool) => {
                    try {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.src = customTool.imageUrl!;
                        await new Promise<void>((resolve, reject) => {
                            img.onload = () => resolve();
                            img.onerror = () => reject(new Error(`Failed to load tool image: ${customTool.imageUrl}`));
                        });
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.drawImage(img, 0, 0);
                            const dataUrl = canvas.toDataURL('image/png');
                            cachedImages[customTool.imageUrl!] = dataUrl;
                            logger.log('[Cache] Preloaded custom tool image:', customTool.imageUrl);
                        }
                    } catch (err) {
                        logger.error('[Cache] Failed to preload custom tool image:', customTool.imageUrl, err);
                        cachedImages[customTool.imageUrl!] = customTool.imageUrl!;
                    }
                });
            await Promise.all(loadPromises);
        }
        
        // Cache company logo URL (not blob, just the URL)
        if (companyProfile && companyProfile.logoUrl) {
            cachedImages[companyProfile.logoUrl] = companyProfile.logoUrl;
            logger.log('[Cache] Stored company logo URL:', companyProfile.logoUrl);
        }
        
        // Load and cache company logo as data URL (one-time load at login)
        let companyLogoDataUrl: string | null = null;
        if (companyProfile) {
            try {
                let logoUrlToLoad: string | null = null;
                
                // Determine which logo URL to load
                if (companyProfile.logoUrl) {
                    logoUrlToLoad = companyProfile.logoUrl;
                    logger.log('[Cache] Loading company logo from logoUrl:', logoUrlToLoad);
                } else if (companyProfile.logoFileName === DEFAULT_COMPANY_LOGO_URL) {
                    // Preferred: local public URL (no Firebase Storage request)
                    logoUrlToLoad = DEFAULT_COMPANY_LOGO_URL;
                    logger.log('[Cache] Loading default company logo from local public URL:', logoUrlToLoad);
                } else if (companyProfile.logoFileName === DEFAULT_COMPANY_LOGO_PATH) {
                    // Fallback: load default logo from Storage if logoUrl is missing
                    const defaultLogoRef = ref(storage, DEFAULT_COMPANY_LOGO_PATH);
                    logoUrlToLoad = await getDownloadURL(defaultLogoRef);
                    logger.log('[Cache] Loading default company logo from Storage:', logoUrlToLoad);
                } else {
                    // Final fallback: always have a default logo without Storage requests
                    logoUrlToLoad = DEFAULT_COMPANY_LOGO_URL;
                    logger.log('[Cache] No company logo set, using local default logo:', logoUrlToLoad);
                }
                
                if (logoUrlToLoad) {
                    // Load image and convert to data URL
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.src = logoUrlToLoad;
                    
                    await new Promise<void>((resolve, reject) => {
                        img.onload = () => resolve();
                        img.onerror = () => reject(new Error('Failed to load company logo image'));
                    });
                    
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0);
                        companyLogoDataUrl = canvas.toDataURL('image/png');
                        logger.log('[Cache] Company logo converted to data URL successfully');
                    }
                }
            } catch (logoError) {
                logger.error('[Cache] Error loading company logo for caching:', logoError);
                // Continue without logo - it's not critical
            }
        }
        
        const cacheData: CachedUserData = {
            timestamp: Date.now(),
            userProfile,
            companyProfile,
            cachedImages,
            companyLogoDataUrl
        };
        
        const cacheKey = `${CACHE_PREFIX}${userId}`;
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        
        logger.log('[Cache] User data cached successfully:', {
            userId,
            hasUserProfile: !!userProfile,
            hasCompanyProfile: !!companyProfile,
            cachedImagesCount: Object.keys(cachedImages).length,
            hasCompanyLogoDataUrl: !!companyLogoDataUrl
        });
    } catch (error) {
        logger.error('[Cache] Error caching user data:', error);
        throw error;
    }
};

// Get cached user data
export const getCachedUserData = (userId: string): CachedUserData | null => {
    try {
        const cacheKey = `${CACHE_PREFIX}${userId}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (!cachedData) {
            return null;
        }
        
        const parsed = JSON.parse(cachedData) as CachedUserData;
        const cacheAge = Date.now() - parsed.timestamp;
        
        // Check if cache is still valid
        if (cacheAge > CACHE_DURATION) {
            logger.log('[Cache] Cache expired, age:', Math.round(cacheAge / 1000 / 60), 'minutes');
            clearUserCache(userId);
            return null;
        }
        
        logger.log('[Cache] Using cached data, age:', Math.round(cacheAge / 1000 / 60), 'minutes');
        return parsed;
    } catch (error) {
        logger.error('[Cache] Error reading cached data:', error);
        return null;
    }
};

// Get cached image URL (returns original URL from cache or original URL)
// Note: We don't use blob URLs because they can't be serialized in sessionStorage
// Browser HTTP cache will handle image caching automatically
export const getCachedImageUrl = (userId: string, originalUrl: string): string => {
    const cachedData = getCachedUserData(userId);
    if (cachedData && cachedData.cachedImages[originalUrl]) {
        return cachedData.cachedImages[originalUrl];
    }
    return originalUrl;
};

// Clear user cache
export const clearUserCache = (userId: string): void => {
    try {
        const cacheKey = `${CACHE_PREFIX}${userId}`;
        sessionStorage.removeItem(cacheKey);
        logger.log('[Cache] User cache cleared:', userId);
    } catch (error) {
        logger.error('[Cache] Error clearing cache:', error);
    }
};

// Clear all user caches (for logout)
export const clearAllUserCaches = (): void => {
    try {
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        });
        logger.log('[Cache] All user caches cleared');
    } catch (error) {
        logger.error('[Cache] Error clearing all caches:', error);
    }
};

// Get user profile from cache or fetch if not cached
export const getUserProfileCached = async (userId: string): Promise<UserProfile | null> => {
    const cachedData = getCachedUserData(userId);
    if (cachedData && cachedData.userProfile) {
        return cachedData.userProfile;
    }
    return await getUserProfile(userId);
};

// Get company profile from cache or fetch if not cached
export const getCompanyProfileCached = async (userId: string): Promise<CompanyProfile | null> => {
    const cachedData = getCachedUserData(userId);
    if (cachedData && cachedData.companyProfile) {
        return cachedData.companyProfile;
    }
    return await getCompanyProfileForUser(userId);
};

// Get cached company logo as data URL (loaded once at login, no Storage requests needed)
export const getCachedCompanyLogoDataUrl = (userId: string): string | null => {
    const cachedData = getCachedUserData(userId);
    if (cachedData && cachedData.companyLogoDataUrl) {
        logger.log('[Cache] Returning cached company logo data URL');
        return cachedData.companyLogoDataUrl;
    }
    logger.log('[Cache] No cached company logo data URL found');
    return null;
};

